import { useRef, useState } from "react";
import { fileUrl } from "@/lib/api";
import api from "@/lib/api";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const MAX_DIM = 2000;
const MAX_BYTES = 4.5 * 1024 * 1024; // stay safely under the backend's 5MB limit

const toBlob = (canvas, type, q) => new Promise((res) => canvas.toBlob(res, type, q));

// Downscale/compress large photos in the browser so big camera/phone images
// (commonly 6-15MB) don't get rejected by the 5MB upload limit.
async function prepareImage(file) {
  if (!/image\/(jpeg|png|webp)/.test(file.type)) return file; // skip svg/gif/etc.
  if (file.size <= MAX_BYTES) return file;
  let bitmap;
  try { bitmap = await createImageBitmap(file); } catch { return file; }
  let { width, height } = bitmap;
  const scale = Math.min(1, MAX_DIM / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
  const keepPng = file.type === "image/png";
  let quality = 0.85;
  let blob = await toBlob(canvas, keepPng ? "image/png" : "image/jpeg", quality);
  while (blob && blob.size > MAX_BYTES && quality > 0.4) {
    quality -= 0.15;
    blob = await toBlob(canvas, "image/jpeg", quality);
  }
  if (!blob || blob.size > file.size) return file;
  const ext = blob.type === "image/png" ? "png" : "jpg";
  const base = (file.name || "image").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.${ext}`, { type: blob.type });
}

export function ImageUpload({ label, value, onChange, hint = "JPG, PNG (max 5MB)", testid, aspect = "aspect-video" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    setBusy(true);
    try {
      const file = await prepareImage(raw);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(fileUrl(data.url));
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — please try a smaller image");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = ""; // allow re-selecting the same file
    }
  };

  return (
    <div>
      {label && <label className="text-sm font-semibold text-slate-700 block mb-1">{label}</label>}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={testid} />
      {value ? (
        <div className={`relative ${aspect} rounded-lg overflow-hidden border border-slate-200 group`}>
          <img src={value} alt="" className="w-full h-full object-cover" />
          <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow"><X size={14} /></button>
          <button type="button" onClick={() => ref.current?.click()} className="absolute inset-0 bg-black/0 group-hover:bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-all grid place-items-center text-sm font-semibold">Replace</button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className={`w-full ${aspect} rounded-lg border-2 border-dashed border-slate-300 grid place-items-center text-slate-400 hover:border-[#22d3ee] hover:text-[#06b6d4] transition-colors`}>
          {busy ? <Loader2 className="animate-spin" /> : <div className="text-center"><ImagePlus className="mx-auto" size={22} /><p className="text-xs mt-1">Click to upload</p><p className="text-[10px] text-slate-400">{hint}</p></div>}
        </button>
      )}
    </div>
  );
}
